import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import ImageUploader from '../../components/ui/image-uploader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Tent, MapPin, Calendar, Users, DollarSign, Plus, Pencil, Trash2,
  ChevronDown, ChevronUp, Eye, ExternalLink, Clock, Loader2, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_TIERS, normaliseTiers, tierFor } from '../../utils/campPricing';

/**
 * Compact pricing preview rendered inside the Section dialog.
 *
 * Matches the `add_to_cart` + `calculatePrice` bundle priority exactly
 * (price_*_halfday → price_*_day → code fallback) so admins can see the
 * parent's receipt for every booking shape before saving:
 *   · À-la-carte 5 × Full-Day
 *   · 5-Day Full-Day bundle
 *   · 5-Day Half-Day bundle (AM-only week / K-2 default)
 *   · 4-Day bundles (for SD 308 JHS sites)
 *
 * Empty / zero inputs are treated as "not set" and fall back to the
 * standard code defaults ($249 / $199) so the preview stays useful
 * during initial setup.
 */
/**
 * Tiered Per-Session Pricing Editor + Live Preview (Feb 2026 rewrite).
 *
 * Replaces the old 6-bundle-fields admin form.  Shows the parent's receipt
 * for every realistic session count (1 through 10) using the current tier
 * table so admins can eyeball the curve before saving.
 *
 * See /app/frontend/src/utils/campPricing.js — must match backend
 * /app/django_backend/camps/pricing_resolver.py exactly.
 */
function TieredPricingEditor({ form, setForm }) {
  const tiers = normaliseTiers(form.pricing_tiers);
  const topRate = tiers[0]?.price ?? 69;

  const updateTier = (idx, field, value) => {
    const next = tiers.map((t, i) => i === idx ? { ...t, [field]: value === '' ? null : Number(value) } : t);
    setForm(p => ({ ...p, pricing_tiers: next }));
  };
  const resetDefaults = () => {
    setForm(p => ({ ...p, pricing_tiers: DEFAULT_TIERS }));
  };

  // Preview matrix — common session counts with active tier + savings.
  const previewRows = [1, 2, 3, 4, 5, 6, 8, 10].map(sessions => {
    const tier = tierFor(sessions, tiers);
    const total = tier.price * sessions;
    const baseline = topRate * sessions;
    const savings = Math.max(0, baseline - total);
    return { sessions, rate: tier.price, total, savings, tierLabel: `$${tier.price}/session` };
  });

  return (
    <div className="space-y-3" data-testid="tiered-pricing-editor">
      {/* Tier editor table */}
      <div className="rounded-lg border border-amber-300 bg-amber-50/50 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">Per-Session Pricing Tiers</span>
          <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={resetDefaults} data-testid="reset-default-tiers-btn">Reset defaults</Button>
        </div>
        <p className="text-[11px] text-amber-900/70 mb-2">
          1 session = 1 half-day (AM or PM). A full day counts as 2 sessions. Each tier applies when the session count falls in that range.
        </p>
        <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1 text-[11px] font-semibold text-amber-900/80 mb-1 px-1">
          <span>Min sessions</span><span>Max sessions</span><span>Price per session ($)</span><span className="w-4" />
        </div>
        {tiers.map((t, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1 mb-1">
            <Input type="number" min="1" value={t.min_sessions ?? ''} onChange={e => updateTier(idx, 'min_sessions', e.target.value)} className="h-8 text-xs" data-testid={`tier-min-${idx}`} />
            <Input type="number" min="1" placeholder="∞" value={t.max_sessions ?? ''} onChange={e => updateTier(idx, 'max_sessions', e.target.value)} className="h-8 text-xs" data-testid={`tier-max-${idx}`} />
            <Input type="number" min="0" step="0.01" value={t.price ?? ''} onChange={e => updateTier(idx, 'price', e.target.value)} className="h-8 text-xs" data-testid={`tier-price-${idx}`} />
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={() => { const next = tiers.filter((_, i) => i !== idx); setForm(p => ({ ...p, pricing_tiers: next })); }} data-testid={`tier-remove-${idx}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="h-7 text-xs mt-1" onClick={() => setForm(p => ({ ...p, pricing_tiers: [...tiers, { min_sessions: (tiers[tiers.length - 1]?.max_sessions || 0) + 1, max_sessions: null, price: 45 }] }))} data-testid="add-tier-btn">
          <Plus className="h-3 w-3 mr-1" /> Add tier
        </Button>
      </div>

      {/* Live receipt preview */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="bundle-pricing-preview">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Live Pricing Preview</span>
          <span className="text-[10px] text-emerald-700/70">What the parent sees at checkout</span>
        </div>
        <div className="space-y-1">
          {previewRows.map(r => (
            <div key={r.sessions} className="flex items-center justify-between text-xs" data-testid={`preview-sessions-${r.sessions}`}>
              <span className="text-gray-700">{r.sessions} session{r.sessions === 1 ? '' : 's'} <span className="text-gray-400">({r.tierLabel})</span></span>
              <span className="flex items-center gap-2">
                <span className="font-semibold tabular-nums">${r.total.toFixed(2)}</span>
                {r.savings > 0 ? (
                  <span className="text-emerald-700 font-medium tabular-nums">Saves ${r.savings.toFixed(2)}</span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminCampsPage({ embedded }) {
  const { api } = useAuth();
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCamp, setExpandedCamp] = useState(null);
  const [locations, setLocations] = useState([]);
  const [courses, setCourses] = useState([]);

  // Camp form dialog
  const [campDialog, setCampDialog] = useState(false);
  const [editingCamp, setEditingCamp] = useState(null);
  const [campForm, setCampForm] = useState({ name: '', description: '', category: '', subcategory: '', skill_level: 'Beginner', active: true });

  // Section form dialog
  const [sectionDialog, setSectionDialog] = useState(false);
  const [sectionCampId, setSectionCampId] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({
    location: '', start_date: '', end_date: '', start_time: '09:00', end_time: '15:00',
    capacity: 20, price_full_camp: '', price_per_day: '',
    price_am_session: '', price_pm_session: '',
    price_5_day: '', price_4_day: '',
    price_3_day: '', price_2_day: '',
    price_5_day_halfday: '', price_4_day_halfday: '',
    pricing_tiers: [],
    active: true, notes: ''
  });

  // Day form dialog
  const [dayDialog, setDayDialog] = useState(false);
  const [dayCampId, setDayCampId] = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [dayForm, setDayForm] = useState({ day_number: '', course: '', session_type: 'full_day', thumbnail_override: '' });
  const [courseSearch, setCourseSearch] = useState('');

  // Per-section schedule-override dialog — lets admin swap or cancel a day's
  // course at ONE location without touching the shared camp template.
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [scheduleSection, setScheduleSection] = useState(null);
  const [scheduleRows, setScheduleRows] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const fetchCamps = useCallback(async () => {
    try {
      const response = await api.get('/camps/?active_only=false&page_size=100');
      const data = response.data.results || response.data;
      // Use Promise.allSettled (NOT all) so one malformed camp's failing
      // detail fetch doesn't take down the entire dashboard.
      // Bug we hit: an admin saved a new camp with no sections/days and the
      // detail endpoint either 500'd or the panel rendered crashed; the
      // dashboard then showed nothing even though all OTHER camps were fine.
      const settled = await Promise.allSettled(
        data.map(c => api.get(`/camps/${c.id}/`).then(r => r.data)),
      );
      const detailed = [];
      const failures = [];
      settled.forEach((s, idx) => {
        if (s.status === 'fulfilled' && s.value) {
          detailed.push(s.value);
        } else {
          // Fall back to the LIST-shape camp (no days/sections) so the row
          // still renders with a "broken" hint and the admin can edit/delete.
          const listCamp = data[idx] || {};
          detailed.push({
            ...listCamp,
            sections: [],
            days: [],
            __detailError: true,
            __detailErrorMessage: s.reason?.message || s.reason?.toString?.() || 'Failed to load detail',
          });
          failures.push({ id: listCamp.id, name: listCamp.name, error: s.reason });
        }
      });
      setCamps(detailed);
      if (failures.length > 0) {
        toast.warning(
          `${failures.length} camp${failures.length > 1 ? 's' : ''} failed to load detail — showing partial data so you can edit/delete them.`,
          { duration: 6000 },
        );
        // eslint-disable-next-line no-console
        console.warn('[AdminCamps] detail fetch failures', failures);
      }
    } catch {
      toast.error('Failed to load camps');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchCamps(); }, [fetchCamps]);

  useEffect(() => {
    api.get('/locations/?page_size=100').then(r => setLocations(r.data.results || r.data || [])).catch(() => {});
    api.get('/courses/?page_size=200&active_only=false').then(r => setCourses(r.data.results || r.data || [])).catch(() => {});
  }, [api]);

  // Camp CRUD
  const openNewCamp = () => {
    setEditingCamp(null);
    setCampForm({ name: '', description: '', category: '', subcategory: '', skill_level: 'Beginner', active: true, thumbnail_url: '' });
    setCampDialog(true);
  };

  const openEditCamp = (camp) => {
    setEditingCamp(camp);
    setCampForm({
      name: camp.name, description: camp.description || '', category: camp.category || '',
      subcategory: camp.subcategory || '', skill_level: camp.skill_level || 'Beginner', active: camp.active,
      thumbnail_url: camp.thumbnail_url || ''
    });
    setCampDialog(true);
  };

  const saveCamp = async () => {
    try {
      if (editingCamp) {
        await api.patch(`/camps/${editingCamp.id}/`, campForm);
        toast.success('Camp updated');
      } else {
        await api.post('/camps/', campForm);
        toast.success('Camp created');
      }
      setCampDialog(false);
      fetchCamps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save camp');
    }
  };

  const deleteCamp = async (id) => {
    if (!window.confirm('Delete this camp and all its days/sections?')) return;
    try {
      await api.delete(`/camps/${id}/`);
      toast.success('Camp deleted');
      fetchCamps();
    } catch {
      toast.error('Failed to delete camp');
    }
  };

  // Section CRUD
  const openNewSection = (campId) => {
    setSectionCampId(campId);
    setEditingSection(null);
    setSectionForm({
      location: '', start_date: '', end_date: '', start_time: '09:00', end_time: '15:00',
      capacity: 20, price_full_camp: '', price_per_day: '',
      price_am_session: '', price_pm_session: '',
      price_5_day: '', price_4_day: '',
      price_3_day: '', price_2_day: '',
      price_5_day_halfday: '', price_4_day_halfday: '',
      pricing_tiers: DEFAULT_TIERS,
      active: true, notes: '', external_url: ''
    });
    setSectionDialog(true);
  };

  const openEditSection = (campId, section) => {
    setSectionCampId(campId);
    setEditingSection(section);
    setSectionForm({
      location: section.location || '', start_date: section.start_date || '', end_date: section.end_date || '',
      start_time: section.start_time || '09:00', end_time: section.end_time || '15:00',
      capacity: section.capacity || 20,
      price_full_camp: section.price_full_camp || '',
      price_per_day: section.price_per_day || '',
      price_am_session: section.price_am_session || '',
      price_pm_session: section.price_pm_session || '',
      price_5_day: section.price_5_day || '',
      price_4_day: section.price_4_day || '',
      price_3_day: section.price_3_day || '',
      price_2_day: section.price_2_day || '',
      price_5_day_halfday: section.price_5_day_halfday || '',
      price_4_day_halfday: section.price_4_day_halfday || '',
      pricing_tiers: section.pricing_tiers && section.pricing_tiers.length ? section.pricing_tiers : (section.effective_pricing_tiers || DEFAULT_TIERS),
      active: section.active, notes: section.notes || '',
      external_url: section.external_url || ''
    });
    setSectionDialog(true);
  };

  const saveSection = async () => {
    try {
      const payload = { ...sectionForm, camp: sectionCampId };
      if (!payload.location) delete payload.location;
      // Strip empty-string decimal fields so DRF doesn't reject them when the
      // admin leaves a price blank (blanks mean "use the code-level default").
      ['price_full_camp','price_per_day','price_am_session','price_pm_session',
       'price_5_day','price_4_day','price_3_day','price_2_day',
       'price_5_day_halfday','price_4_day_halfday']
        .forEach(k => { if (payload[k] === '' || payload[k] === null) delete payload[k]; });
      // Normalise pricing_tiers — strip any incomplete rows so the backend
      // receives only valid {min_sessions,max_sessions,price} entries.
      if (Array.isArray(payload.pricing_tiers)) {
        payload.pricing_tiers = payload.pricing_tiers
          .filter(t => Number.isFinite(Number(t.min_sessions)) && Number.isFinite(Number(t.price)))
          .map(t => ({
            min_sessions: Number(t.min_sessions),
            max_sessions: t.max_sessions === '' || t.max_sessions === null ? null : Number(t.max_sessions),
            price: Number(t.price),
          }));
      }
      if (editingSection) {
        await api.patch(`/camp-sections/${editingSection.id}/`, payload);
        toast.success('Section updated');
      } else {
        await api.post(`/camps/${sectionCampId}/add-section/`, payload);
        toast.success('Section added');
      }
      setSectionDialog(false);
      fetchCamps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save section');
    }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm('Delete this camp section?')) return;
    try {
      await api.delete(`/camp-sections/${sectionId}/`);
      toast.success('Section deleted');
      fetchCamps();
    } catch {
      toast.error('Failed to delete section');
    }
  };

  // Day CRUD
  const openAddDay = (campId, nextDayNum) => {
    setDayCampId(campId);
    setEditingDay(null);
    setDayForm({ day_number: nextDayNum, course: '', session_type: 'full_day', thumbnail_override: '' });
    setCourseSearch('');
    setDayDialog(true);
  };

  const openEditDay = (campId, day) => {
    setDayCampId(campId);
    setEditingDay(day);
    setDayForm({ day_number: day.day_number, course: day.course, session_type: day.session_type || 'full_day', thumbnail_override: day.thumbnail_override || '' });
    setCourseSearch('');
    setDayDialog(true);
  };

  const saveDay = async () => {
    try {
      if (editingDay) {
        await api.patch(`/camps/${dayCampId}/edit-day/${editingDay.id}/`, dayForm);
        toast.success('Day updated');
      } else {
        await api.post(`/camps/${dayCampId}/add-day/`, dayForm);
        toast.success('Day added');
      }
      setDayDialog(false);
      fetchCamps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save day');
    }
  };

  const deleteDay = async (campId, dayId) => {
    if (!window.confirm('Remove this day from the camp?')) return;
    try {
      await api.delete(`/camps/${campId}/delete-day/${dayId}/`);
      toast.success('Day removed');
      fetchCamps();
    } catch {
      toast.error('Failed to delete day');
    }
  };

  // ─── Schedule override handlers ──────────────────────────────────────
  // Admin-only flow to change course / cancel a day at a single location.
  // Wraps the DRF `/api/camp-sections/<id>/schedule/` + `/overrides/` routes.
  const openSchedule = async (campId, sec) => {
    setScheduleSection({ ...sec, camp_id: campId });
    setScheduleDialog(true);
    setScheduleLoading(true);
    try {
      const { data } = await api.get(`/camp-sections/${sec.id}/schedule/`);
      setScheduleRows(data.schedule || []);
    } catch {
      toast.error('Failed to load schedule');
      setScheduleRows([]);
    } finally {
      setScheduleLoading(false);
    }
  };

  const saveScheduleOverride = async (row, patch) => {
    if (!scheduleSection) return;
    setScheduleSaving(true);
    try {
      const body = {
        day_number: row.day_number,
        session_type: row.session_type,
        course_id: patch.course_id ?? row.course_id,
        cancelled: patch.cancelled ?? row.cancelled,
        notes: patch.notes ?? row.override_notes ?? '',
      };
      const { data } = await api.put(
        `/camp-sections/${scheduleSection.id}/overrides/`,
        body,
      );
      // Apply the new values locally so the grid updates without a refetch.
      setScheduleRows(prev => prev.map(r =>
        r.day_number === row.day_number && r.session_type === row.session_type
          ? {
              ...r,
              course_id: body.course_id,
              course_title: (courses.find(c => c.id === body.course_id) || {}).title || r.course_title,
              cancelled: !!body.cancelled,
              override_id: data.override_id,
              override_notes: body.notes,
            }
          : r,
      ));
      if (data.enrollments_soft_cancelled) {
        toast.success(`Saved · ${data.enrollments_soft_cancelled} existing enrollment(s) soft-cancelled`);
      } else {
        toast.success('Schedule updated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  const revertScheduleOverride = async (row) => {
    if (!scheduleSection || !row.override_id) return;
    setScheduleSaving(true);
    try {
      await api.delete(
        `/camp-sections/${scheduleSection.id}/overrides/${row.day_number}/${row.session_type}/`,
      );
      // Refetch the single section's schedule to pick up base template values.
      const { data } = await api.get(`/camp-sections/${scheduleSection.id}/schedule/`);
      setScheduleRows(data.schedule || []);
      toast.success('Reverted to camp template');
    } catch {
      toast.error('Failed to revert');
    } finally {
      setScheduleSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-camps-page">
      <div className="flex justify-between items-center">
        {!embedded && (
        <div>
          <h1 className="font-heading text-2xl font-bold">Summer Camps</h1>
          <p className="text-muted-foreground">Manage multi-day camp programs, schedules, and pricing</p>
        </div>
        )}
        <div className={`flex gap-2 ${embedded ? 'ml-auto' : ''}`}>
          <Badge variant="outline" className="text-sm">{camps.length} camps</Badge>
          <Button onClick={openNewCamp} data-testid="create-camp-btn"><Plus className="h-4 w-4 mr-1" /> New Camp</Button>
        </div>
      </div>

      {camps.map((camp) => {
        const isExpanded = expandedCamp === camp.id;
        return (
          <Card key={camp.id} className="card-dashboard" data-testid={`camp-card-${camp.id}`}>
            <CardContent className="p-6">
              {/* Camp header */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2 cursor-pointer" onClick={() => setExpandedCamp(isExpanded ? null : camp.id)}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tent className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-lg">{camp.name}</h3>
                    <Badge className={camp.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                      {camp.active ? 'Active' : 'Inactive'}
                    </Badge>
                    {camp.__detailError && (
                      <Badge className="bg-red-100 text-red-700 border border-red-300" title={camp.__detailErrorMessage} data-testid={`camp-broken-badge-${camp.id}`}>
                        ⚠ Broken — open or delete
                      </Badge>
                    )}
                    {isExpanded ? <ChevronUp className="h-4 w-4 ml-auto text-muted-foreground" /> : <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{camp.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar className="h-4 w-4" /> {camp.total_days ?? 0} days</span>
                    <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {camp.sections?.length || 0} locations</span>
                    <span className="flex items-center gap-1"><DollarSign className="h-4 w-4" /> ${camp.full_camp_price ?? '—'} full camp</span>
                    {camp.category && <Badge variant="outline" className="capitalize">{camp.category}</Badge>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => window.location.assign(`/admin/camps/${camp.id}`)} data-testid={`open-camp-${camp.id}`} title="Open detail page">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(`/camps/${camp.id}`, '_blank')} data-testid={`view-camp-${camp.id}`}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEditCamp(camp)} data-testid={`edit-camp-${camp.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-destructive" onClick={() => deleteCamp(camp.id)} data-testid={`delete-camp-${camp.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Expanded: Days & Sections */}
              {isExpanded && (
                <div className="mt-6 space-y-6 border-t pt-6">
                  {/* Days */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold">Camp Days ({camp.days?.length || 0})</h4>
                      <Button size="sm" variant="outline" onClick={() => openAddDay(camp.id, (camp.days?.length || 0) + 1)} data-testid={`add-day-${camp.id}`}>
                        <Plus className="h-3 w-3 mr-1" /> Add Day
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {camp.days?.map(day => (
                        <div key={day.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg text-sm" data-testid={`admin-day-${day.day_number}`}>
                          <Badge className="bg-primary text-primary-foreground shrink-0">Day {day.day_number}</Badge>
                          {day.thumbnail_url && (
                            <img src={day.thumbnail_url} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                          )}
                          <span className="font-medium flex-1">{day.course_title}</span>
                          <span className="text-muted-foreground">${day.course_price}</span>
                          {day.has_kit && <Badge variant="outline" className="text-xs">{day.kit_name}</Badge>}
                          <div className="flex gap-1 shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => openEditDay(camp.id, day)} data-testid={`edit-day-${day.id}`}><Pencil className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteDay(camp.id, day.id)} data-testid={`delete-day-${day.id}`}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sections */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold">Locations & Schedule ({camp.sections?.length || 0})</h4>
                      <Button size="sm" variant="outline" onClick={() => openNewSection(camp.id)} data-testid={`add-section-${camp.id}`}>
                        <Plus className="h-3 w-3 mr-1" /> Add Location
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {camp.sections?.map(sec => (
                        <div key={sec.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg text-sm" data-testid={`admin-section-${sec.id}`}>
                          <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-medium">{sec.location_name}</span>
                          {sec.external_url && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs"><ExternalLink className="h-3 w-3 mr-1" /> External</Badge>
                          )}
                          {sec.start_date && <span className="text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" /> {sec.start_date} — {sec.end_date}</span>}
                          {sec.start_time && <span className="text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {sec.start_time}–{sec.end_time}</span>}
                          <span className="text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> {sec.spots_remaining}/{sec.capacity}</span>
                          <span className="text-muted-foreground">${sec.price_full_camp}/wk · ${sec.price_per_day}/day</span>
                          <div className="ml-auto flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openSchedule(camp.id, sec)} data-testid={`edit-schedule-${sec.id}`} title="Edit per-day schedule (swap / cancel courses)">
                              <Calendar className="h-3 w-3 mr-1" /> Schedule
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditSection(camp.id, sec)} data-testid={`edit-section-${sec.id}`}><Pencil className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteSection(sec.id)} data-testid={`delete-section-${sec.id}`}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {camps.length === 0 && (
        <Card className="card-dashboard">
          <CardContent className="p-12 text-center text-muted-foreground">
            <Tent className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg mb-4">No camps created yet</p>
            <Button onClick={openNewCamp}><Plus className="h-4 w-4 mr-1" /> Create Your First Camp</Button>
          </CardContent>
        </Card>
      )}

      {/* Camp Dialog */}
      <Dialog open={campDialog} onOpenChange={setCampDialog}>
        <DialogContent className="max-w-lg" data-testid="camp-dialog">
          <DialogHeader><DialogTitle>{editingCamp ? 'Edit Camp' : 'New Camp'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={campForm.name} onChange={e => setCampForm(p => ({...p, name: e.target.value}))} placeholder="e.g., Robotics Mastery Camp" data-testid="camp-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={campForm.description} onChange={e => setCampForm(p => ({...p, description: e.target.value}))} rows={3} data-testid="camp-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Input value={campForm.category} onChange={e => setCampForm(p => ({...p, category: e.target.value}))} placeholder="e.g., robotics" />
              </div>
              <div>
                <Label>Subcategory</Label>
                <Input value={campForm.subcategory} onChange={e => setCampForm(p => ({...p, subcategory: e.target.value}))} placeholder="e.g., advanced" />
              </div>
            </div>
            <div>
              <Label>Thumbnail URL <span className="text-xs text-muted-foreground">(recommended: 1280 x 480px, 8:3 ratio)</span></Label>
              <Input value={campForm.thumbnail_url} onChange={e => setCampForm(p => ({...p, thumbnail_url: e.target.value}))} placeholder="https://example.com/image.jpg" data-testid="camp-thumbnail-input" />
              {campForm.thumbnail_url && (
                <img src={campForm.thumbnail_url} alt="Preview" className="mt-2 h-20 w-full object-cover rounded-md border" onError={e => { e.target.style.display = 'none'; }} />
              )}
            </div>
            <div className="flex items-center gap-3">
              <Label>Active</Label>
              <Switch checked={campForm.active} onCheckedChange={v => setCampForm(p => ({...p, active: v}))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCampDialog(false)}>Cancel</Button>
            <Button onClick={saveCamp} disabled={!campForm.name} data-testid="save-camp-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Section Dialog */}
      <Dialog open={sectionDialog} onOpenChange={setSectionDialog}>
        <DialogContent className="max-w-lg" data-testid="section-dialog">
          <DialogHeader><DialogTitle>{editingSection ? 'Edit Location/Schedule' : 'Add Location/Schedule'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Location</Label>
              <Select value={sectionForm.location} onValueChange={v => setSectionForm(p => ({...p, location: v}))}>
                <SelectTrigger data-testid="section-location-select"><SelectValue placeholder="Select location..." /></SelectTrigger>
                <SelectContent>
                  {locations.map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" value={sectionForm.start_date} onChange={e => setSectionForm(p => ({...p, start_date: e.target.value}))} /></div>
              <div><Label>End Date</Label><Input type="date" value={sectionForm.end_date} onChange={e => setSectionForm(p => ({...p, end_date: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Time</Label><Input type="time" value={sectionForm.start_time} onChange={e => setSectionForm(p => ({...p, start_time: e.target.value}))} /></div>
              <div><Label>End Time</Label><Input type="time" value={sectionForm.end_time} onChange={e => setSectionForm(p => ({...p, end_time: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Capacity</Label><Input type="number" value={sectionForm.capacity} onChange={e => setSectionForm(p => ({...p, capacity: parseInt(e.target.value) || 0}))} /></div>
              <div><Label>Full Camp $ <span className="text-[10px] text-muted-foreground">(marketing only)</span></Label><Input type="number" step="0.01" value={sectionForm.price_full_camp} onChange={e => setSectionForm(p => ({...p, price_full_camp: e.target.value}))} data-testid="section-price-full" /></div>
              <div><Label>Per Day $ <span className="text-[10px] text-muted-foreground">(marketing only)</span></Label><Input type="number" step="0.01" value={sectionForm.price_per_day} onChange={e => setSectionForm(p => ({...p, price_per_day: e.target.value}))} data-testid="section-price-day" /></div>
            </div>
            {/* Legacy bundle fields removed from UI Feb 2026. Camp pricing
                is now driven entirely by the TieredPricingEditor below —
                the backend resolver ignores price_5_day / price_4_day / etc.
                Columns remain in the DB for audit; safe to delete later. */}

            {/* ─── Tiered Per-Session Pricing Editor ───────────────────────
                Admin-editable tiers + live receipt preview. Defaults to
                1→$69, 2-4→$59, 5-9→$49, 10+→$45. Session counts are
                cart-level: 1 half-day = 1 session, full day = 2 sessions.
            */}
            <TieredPricingEditor form={sectionForm} setForm={setSectionForm} />

            <div><Label>Notes</Label><Textarea value={sectionForm.notes} onChange={e => setSectionForm(p => ({...p, notes: e.target.value}))} rows={2} /></div>
            <div>
              <Label>External Registration URL <span className="text-xs text-muted-foreground">(optional — for partner sites like park districts)</span></Label>
              <Input
                value={sectionForm.external_url}
                onChange={e => setSectionForm(p => ({...p, external_url: e.target.value}))}
                placeholder="https://parkdistrict.example.com/register"
                data-testid="section-external-url"
              />
            </div>
            <div className="flex items-center gap-3">
              <Label>Active</Label>
              <Switch checked={sectionForm.active} onCheckedChange={v => setSectionForm(p => ({...p, active: v}))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionDialog(false)}>Cancel</Button>
            <Button onClick={saveSection} data-testid="save-section-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day Dialog */}
      <Dialog open={dayDialog} onOpenChange={(open) => { setDayDialog(open); if (!open) setCourseSearch(''); }}>
        <DialogContent className="max-w-md" data-testid="day-dialog">
          <DialogHeader><DialogTitle>{editingDay ? 'Edit Camp Day' : 'Add Camp Day'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Day Number</Label>
                <Input type="number" min="1" value={dayForm.day_number} onChange={e => setDayForm(p => ({...p, day_number: parseInt(e.target.value) || ''}))} />
              </div>
              <div>
                <Label>Session Type</Label>
                <Select value={dayForm.session_type} onValueChange={v => setDayForm(p => ({...p, session_type: v}))}>
                  <SelectTrigger data-testid="day-session-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_day">Full Day (9 AM – 4 PM)</SelectItem>
                    <SelectItem value="am">AM Only (9 AM – 12 PM)</SelectItem>
                    <SelectItem value="pm">PM Only (1 PM – 4 PM)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Course</Label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search courses..."
                  value={courseSearch}
                  onChange={e => setCourseSearch(e.target.value)}
                  className="pl-9"
                  data-testid="course-search-input"
                />
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-md">
                {courses
                  .filter(c => !courseSearch || c.title.toLowerCase().includes(courseSearch.toLowerCase()))
                  .map(c => (
                    <div
                      key={c.id}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-muted transition-colors ${dayForm.course === c.id ? 'bg-primary/10 font-medium text-primary' : ''}`}
                      onClick={() => setDayForm(p => ({...p, course: c.id}))}
                      data-testid={`course-option-${c.id}`}
                    >
                      {c.title}
                    </div>
                  ))
                }
                {courses.filter(c => !courseSearch || c.title.toLowerCase().includes(courseSearch.toLowerCase())).length === 0 && (
                  <div className="px-3 py-4 text-sm text-muted-foreground text-center">No courses found</div>
                )}
              </div>
              {dayForm.course && (
                <p className="text-xs text-muted-foreground mt-1">Selected: {courses.find(c => c.id === dayForm.course)?.title}</p>
              )}
            </div>
            <div>
              <ImageUploader
                label="Image URL — recommended 500×800 (5:8 portrait)"
                value={dayForm.thumbnail_override}
                onChange={(url) => setDayForm(p => ({...p, thumbnail_override: url}))}
                folder="camp-day-thumbs"
                testidPrefix="day-thumbnail"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDayDialog(false); setCourseSearch(''); }}>Cancel</Button>
            <Button onClick={saveDay} disabled={!dayForm.course} data-testid="save-day-btn">{editingDay ? 'Save Changes' : 'Add Day'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Per-section Schedule Override Dialog ─────────────────────────
          Matrix of (day_number × session_type) cells. Each cell shows the
          effective course at this location + a quick "Change course" and
          "Cancel" action. Cancellations soft-cancel existing enrollments
          at this location. "Revert" removes the override so the section
          returns to the shared camp template. */}
      <Dialog open={scheduleDialog} onOpenChange={setScheduleDialog}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="schedule-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              Edit Schedule — {scheduleSection?.location_name || ''}
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Overrides apply to <strong>this location only</strong>. Leave a row un-edited to inherit the shared camp template.
            </p>
          </DialogHeader>
          {scheduleLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : scheduleRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No scheduled days on this camp yet. Add days to the camp first.
            </p>
          ) : (
            <div className="space-y-2">
              {scheduleRows.map(row => {
                const dayLabel = [
                  'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun',
                ][row.day_number - 1] || `Day ${row.day_number}`;
                const sessLabel = {
                  am: 'AM (9–12)', pm: 'PM (1–4)', full_day: 'Full Day (9–4)',
                }[row.session_type] || row.session_type;
                const testidRoot = `schedule-row-${row.day_number}-${row.session_type}`;
                return (
                  <div
                    key={`${row.day_number}-${row.session_type}`}
                    className={`grid grid-cols-[80px_1fr_auto] gap-3 items-center p-3 rounded-lg border ${
                      row.cancelled ? 'bg-red-50 border-red-200' : row.override_id ? 'bg-amber-50 border-amber-200' : 'bg-muted/40'
                    }`}
                    data-testid={testidRoot}
                  >
                    <div className="text-sm">
                      <div className="font-semibold">{dayLabel}</div>
                      <div className="text-xs text-muted-foreground">{sessLabel}</div>
                    </div>
                    <div className="min-w-0">
                      <select
                        value={row.course_id || ''}
                        disabled={row.cancelled || scheduleSaving}
                        onChange={e => saveScheduleOverride(row, { course_id: e.target.value })}
                        className="w-full border rounded px-2 py-1 text-sm bg-white disabled:opacity-60"
                        data-testid={`${testidRoot}-course`}
                      >
                        {courses.map(c => (
                          <option key={c.id} value={c.id}>{c.title}</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {row.cancelled && (
                          <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid={`${testidRoot}-cancelled-badge`}>
                            CANCELLED
                          </Badge>
                        )}
                        {!row.cancelled && row.override_id && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid={`${testidRoot}-custom-badge`}>
                            CUSTOM
                          </Badge>
                        )}
                        {row.override_notes && (
                          <span className="text-[11px] text-muted-foreground italic">
                            {row.override_notes}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      {!row.cancelled ? (
                        <Button
                          size="sm" variant="outline"
                          disabled={scheduleSaving}
                          onClick={() => {
                            const reason = window.prompt(
                              `Cancel ${dayLabel} ${sessLabel}?\n\nThis immediately soft-cancels any existing enrollments at ${scheduleSection?.location_name}. Add an optional reason (shown internally):`,
                              '',
                            );
                            if (reason === null) return; // user hit cancel
                            saveScheduleOverride(row, { cancelled: true, notes: reason });
                          }}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`${testidRoot}-cancel-btn`}
                        >
                          Cancel day
                        </Button>
                      ) : (
                        <Button
                          size="sm" variant="outline"
                          disabled={scheduleSaving}
                          onClick={() => saveScheduleOverride(row, { cancelled: false, notes: '' })}
                          data-testid={`${testidRoot}-uncancel-btn`}
                        >
                          Un-cancel
                        </Button>
                      )}
                      {row.override_id && (
                        <Button
                          size="sm" variant="ghost"
                          disabled={scheduleSaving}
                          onClick={() => revertScheduleOverride(row)}
                          className="text-xs"
                          data-testid={`${testidRoot}-revert-btn`}
                        >
                          Revert to template
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
